import { FC, PropsWithChildren, ReactNode, useCallback, useMemo } from 'react';

import { normalizeToPlainText } from '@eneco/common/string';
import { Popover } from '@eneco/sparky';
import { useGTM, useTracking } from '@eneco/tracking';

interface Props {
  trigger: ReactNode;
  title: string;
  /** The Sitecore component type used for tracking */
  componentType?: string;
  side?: 'top' | 'right' | 'bottom' | 'left';
  size?: 'small' | 'large';
}

export const TrackedPopover: FC<PropsWithChildren<Props>> = ({
  children,
  title,
  componentType = 'popover',
  trigger,
  side,
  size,
}) => {
  const { scope } = useTracking();
  const { pushToDataLayer } = useGTM();
  const normalizedTitle = useMemo(() => normalizeToPlainText(title), [title]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      pushToDataLayer({
        event: 'select_tooltip',
        selected_content: normalizedTitle,
        selected_action: open ? 'open' : 'close',
        component_name: scope,
        component_type: componentType,
      });
    },
    [pushToDataLayer, normalizedTitle, scope, componentType],
  );

  return (
    <Popover title={normalizedTitle} trigger={trigger} side={side} size={size} onOpenChange={handleOpenChange}>
      {children}
    </Popover>
  );
};

TrackedPopover.displayName = 'TrackedPopover';
